import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

const API_BASE = environment.apiUrl;

export type PairingStatus = 'waiting' | 'paired' | 'active' | 'expired';

export interface StartPairingResponse {
  success: boolean;
  data: { uuid: string; pairing_url: string; expires_at: string };
}
export interface PairingStatusResponse {
  success: boolean;
  data: { uuid: string; status: PairingStatus };
}

const TV_STORAGE_KEY   = 'xo_tv_pairing_uuid';
const IPAD_STORAGE_KEY = 'xo_ipad_pairing_uuid';

@Injectable({ providedIn: 'root' })
export class PairingService {
  private http = inject(HttpClient);

  /** True once 'pairing.reset' arrives on the booth channel (see AppComponent) —
   *  the TV was reconfigured to a new booth and this iPad must re-scan. */
  sessionInvalidated = signal(false);

  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  // ── TV-side storage ────────────────────────────────────────────────────
  getSavedTvUuid(): string | null { return localStorage.getItem(TV_STORAGE_KEY); }
  saveTvUuid(uuid: string): void  { localStorage.setItem(TV_STORAGE_KEY, uuid); }
  clearTvUuid(): void             { localStorage.removeItem(TV_STORAGE_KEY); }

  // ── iPad-side storage ───────────────────────────────────────────────────
  getSavedIpadUuid(): string | null { return localStorage.getItem(IPAD_STORAGE_KEY); }
  saveIpadUuid(uuid: string): void  { localStorage.setItem(IPAD_STORAGE_KEY, uuid); }
  clearIpadUuid(): void             { localStorage.removeItem(IPAD_STORAGE_KEY); }

  // ── API calls ─────────────────────────────────────────────────────────
  start(): Promise<StartPairingResponse> {
    return firstValueFrom(this.http.post<StartPairingResponse>(`${API_BASE}/stand/pairing/start`, {}));
  }

  status(uuid: string): Promise<PairingStatusResponse> {
    return firstValueFrom(this.http.get<PairingStatusResponse>(`${API_BASE}/stand/pairing/${uuid}/status`));
  }

  join(uuid: string): Promise<{ success: boolean; data?: { uuid: string; status: PairingStatus }; message?: string }> {
    return firstValueFrom(this.http.post<any>(`${API_BASE}/stand/pairing/${uuid}/join`, {}));
  }

  reset(uuid: string): Promise<{ success: boolean }> {
    return firstValueFrom(this.http.post<{ success: boolean }>(`${API_BASE}/stand/pairing/${uuid}/reset`, {}));
  }

  private heartbeat(uuid: string): Promise<{ success: boolean; message?: string }> {
    return firstValueFrom(this.http.post<any>(`${API_BASE}/stand/pairing/${uuid}/heartbeat`, {}));
  }

  startHeartbeatLoop(uuid: string): void {
    this.stopHeartbeatLoop();
    this.heartbeatTimer = setInterval(() => {
      this.heartbeat(uuid).catch(() => { /* transient network hiccup, ignore */ });
    }, 45000);
  }

  stopHeartbeatLoop(): void {
    if (this.heartbeatTimer) { clearInterval(this.heartbeatTimer); this.heartbeatTimer = null; }
  }

  flagInvalidated(): void {
    this.stopHeartbeatLoop();
    this.sessionInvalidated.set(true);
  }

  clearInvalidatedFlag(): void {
    this.sessionInvalidated.set(false);
  }

  // ── Cold-boot recovery via guest_id ─────────────────────────────────────
  fetchPairingUuidForGuest(guestId: number): Promise<{ success: boolean; data?: { pairing_uuid: string | null } }> {
    return firstValueFrom(this.http.get<any>(`${API_BASE}/stand/guest/${guestId}/pairing-uuid`));
  }

  /**
   * Cold-boot recovery: if this browser context never went through /pair
   * itself (e.g. the iOS app's deeplink return opened a brand-new
   * private/incognito window that never scanned the QR), localStorage has
   * no pairing_uuid at all. Since we DO know which guest this is, ask the
   * backend — which already stored the guest's pairing_uuid at creation
   * time — and restore from there instead. Saves it to localStorage too,
   * so every subsequent screen in this same tab (lead-form, share, etc.)
   * just works normally from here on.
   */
  async restoreFromGuestId(guestId: number): Promise<string | null> {
    try {
      const res = await this.fetchPairingUuidForGuest(guestId);
      const uuid = res?.data?.pairing_uuid ?? null;
      if (uuid) this.saveIpadUuid(uuid);
      return uuid;
    } catch {
      return null;
    }
  }
}
